import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import './RoadTripChat.css';

function RoadTripChat({ tripId, user, isHost, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Listen for messages
  useEffect(() => {
    const messagesRef = collection(db, 'roadTrips', tripId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(msgs);
      },
      (err) => {
        // Only log non-permission errors
        if (err.code !== 'permission-denied') {
          console.warn('RoadTripChat listener error:', err);
        }
      }
    );

    return () => unsubscribe();
  }, [tripId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await addDoc(collection(db, 'roadTrips', tripId, 'messages'), {
        text: newMessage.trim(),
        userId: user.uid,
        userName: user.displayName || user.email.split('@')[0],
        timestamp: serverTimestamp(),
      });
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Quick messages for Road Trip context
  const quickMessages = [
    '🎵 Great song!',
    '🚗 Road trip!',
    '👍 Love it!',
    '🎤 Sing along!',
    '⏭️ Skip this?',
    '💬 Talk to host',
  ];

  const sendQuickMessage = (msg) => {
    setNewMessage(msg);
  };

  return (
    <div className="roadtrip-chat-overlay">
      <div className="roadtrip-chat">
        <div className="chat-header">
          <h3>
            💬 Road Trip Chat{' '}
            {isHost && <span className="host-badge">👑 Host</span>}
          </h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>👋 Say hello to your road trip crew!</p>
              {!isHost && (
                <p className="host-hint">
                  💡 Message the host if you have song requests or questions
                </p>
              )}
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-message ${
                  msg.userId === user.uid ? 'own' : ''
                }`}
              >
                {msg.userId !== user.uid && (
                  <span className="message-sender">{msg.userName}</span>
                )}
                <div className="message-content">
                  <p>{msg.text}</p>
                  <span className="message-time">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Messages */}
        <div className="quick-messages">
          {quickMessages.map((msg, idx) => (
            <button
              key={idx}
              className="quick-msg-btn"
              onClick={() => sendQuickMessage(msg)}
            >
              {msg}
            </button>
          ))}
        </div>

        <form className="chat-input-form" onSubmit={handleSend}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isHost ? 'Type a message...' : 'Message the host...'}
            maxLength={500}
          />
          <button type="submit" disabled={!newMessage.trim() || sending}>
            {sending ? '...' : '➤'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RoadTripChat;
